import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import {
  PencilIcon, TrashIcon, PlusIcon, DocumentIcon,
  CalendarIcon, UserGroupIcon, FlagIcon, ClockIcon,
  CurrencyDollarIcon, ChartBarIcon, TagIcon, BuildingOfficeIcon,
  UserIcon, CheckCircleIcon, ExclamationTriangleIcon,
  ArrowRightIcon, ChatBubbleLeftRightIcon,
  CalendarDaysIcon, ListBulletIcon, FireIcon, TrophyIcon,
  ArrowPathIcon, MagnifyingGlassIcon, XMarkIcon,
  ShieldCheckIcon, EnvelopeIcon
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleIconSolid
} from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { PageHeader } from '../../components/layout';
import {
  Card, CardBody, CardHeader, CardTitle, Button, Avatar, AvatarGroup,
  StatusBadge, PriorityBadge, ProgressBar, PageLoader, EmptyState,
  Tabs, TabList, Tab, TabPanels, TabPanel,
  Modal, ModalBody, ModalFooter, Input, Textarea, Badge, Select
} from '../../components/ui';
import { projectService, taskService } from '../../services';
import { useUIStore, useAuthStore } from '../../stores';
import { useForm, Controller } from 'react-hook-form';

dayjs.extend(relativeTime);

export default function ProjectDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { confirm } = useUIStore();
  const { user: currentUser } = useAuthStore();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showMilestoneModal, setShowMilestoneModal] = useState(false);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileSearchQuery, setFileSearchQuery] = useState('');
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showEditMemberModal, setShowEditMemberModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  const { register: registerTask, handleSubmit: handleTaskSubmit, reset: resetTask } = useForm();
  const { register: registerNote, handleSubmit: handleNoteSubmit, reset: resetNote } = useForm();
  const { register: registerMilestone, handleSubmit: handleMilestoneSubmit, reset: resetMilestone } = useForm();
  const { register: registerFile, handleSubmit: handleFileSubmit, reset: resetFile, control: controlFile } = useForm();
  // Separate form instances for add and edit to avoid validation conflicts
  const { register: registerMemberAdd, handleSubmit: handleMemberSubmitAdd, reset: resetMemberAdd, setValue: setMemberValueAdd, control: controlMemberAdd } = useForm({
    defaultValues: {
      email: '',
      role: 'contributor',
      permissions: {
        canEditProject: false,
        canManageFiles: true,
        canManageNotes: true,
        canManageTasks: false,
        canManageMilestones: false,
        canManageMembers: false,
        canViewFinancials: false,
        canChat: true
      }
    }
  });

  const { register: registerMemberEdit, handleSubmit: handleMemberSubmitEdit, reset: resetMemberEdit, setValue: setMemberValueEdit, control: controlMemberEdit } = useForm({
    defaultValues: {
      role: 'contributor',
      permissions: {
        canEditProject: false,
        canManageFiles: true,
        canManageNotes: true,
        canManageTasks: false,
        canManageMilestones: false,
        canManageMembers: false,
        canViewFinancials: false,
        canChat: true
      }
    }
  });

  useEffect(() => {
    fetchProject();
    fetchTasks();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProject = async () => {
    try {
      const data = await projectService.getProject(id);
      setProject(data);
    } catch (error) {
      toast.error(t('errors.notFound'));
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await taskService.getTasks(id, { limit: 100 });
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: t('common.delete'),
      message: 'Are you sure you want to delete this project?',
      confirmText: t('common.delete'),
      variant: 'danger'
    });

    if (confirmed) {
      try {
        await projectService.deleteProject(id);
        toast.success(t('success.deleted'));
        navigate('/projects');
      } catch (error) {
        toast.error(error.response?.data?.message || t('errors.serverError'));
      }
    }
  };

  const onTaskSubmit = async (data) => {
    try {
      await taskService.createTask(id, data);
      toast.success(t('success.created'));
      setShowTaskModal(false);
      resetTask();
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    }
  };

  const onNoteSubmit = async (data) => {
    try {
      await projectService.addNote(id, data);
      toast.success(t('success.created'));
      setShowNoteModal(false);
      resetNote();
      fetchProject();
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    }
  };

  const getRolePermissions = (role) => {
    const permissions = {
      owner: {
        canEditProject: true,
        canManageFiles: true,
        canManageNotes: true,
        canManageTasks: true,
        canManageMilestones: true,
        canManageMembers: true,
        canViewFinancials: true,
        canChat: true
      },
      manager: {
        canEditProject: true,
        canManageFiles: true,
        canManageNotes: true,
        canManageTasks: true,
        canManageMilestones: true,
        canManageMembers: false,
        canViewFinancials: true,
        canChat: true
      },
      contributor: {
        canEditProject: false,
        canManageFiles: true,
        canManageNotes: true,
        canManageTasks: false,
        canManageMilestones: false,
        canManageMembers: false,
        canViewFinancials: false,
        canChat: true
      },
      viewer: {
        canEditProject: false,
        canManageFiles: false,
        canManageNotes: false,
        canManageTasks: false,
        canManageMilestones: false,
        canManageMembers: false,
        canViewFinancials: false,
        canChat: true
      }
    };
    return permissions[role] || permissions.contributor;
  };

  const onAddMember = async (data) => {
    try {
      const rolePerms = getRolePermissions(data.role);
      const hasCustomPerms = Object.keys(data.permissions).some(key => data.permissions[key] !== rolePerms[key]);

      const memberData = {
        email: data.email,
        role: data.role,
        ...(hasCustomPerms && { permissions: data.permissions })
      };

      await projectService.addMember(id, memberData);
      toast.success(t('success.created'));
      setShowAddMemberModal(false);
      resetMemberAdd();
      fetchProject();
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    }
  };

  const onUpdateMember = async (data) => {
    console.log('onUpdateMember called with data:', data);
    console.log('selectedMember:', selectedMember);

    if (!selectedMember) {
      toast.error('No member selected');
      return;
    }

    try {
      // Get the user ID from the selected member
      // Try multiple ways to extract the user ID
      let memberUserId = null;

      // First, try the userId we stored explicitly
      if (selectedMember.userId) {
        memberUserId = typeof selectedMember.userId === 'string'
          ? selectedMember.userId
          : selectedMember.userId.toString();
      }
      // If user is an object with _id
      else if (selectedMember.user?._id) {
        memberUserId = selectedMember.user._id.toString();
      }
      // If user is already a string/ObjectId
      else if (selectedMember.user && typeof selectedMember.user === 'string') {
        memberUserId = selectedMember.user;
      }
      // Check if user is an ObjectId object
      else if (selectedMember.user && selectedMember.user.toString) {
        memberUserId = selectedMember.user.toString();
      }
      // Last resort: check if memberData had a user property
      else if (selectedMember.user) {
        memberUserId = selectedMember.user.toString();
      }

      console.log('Extracted memberUserId:', memberUserId);

      if (!memberUserId) {
        console.error('Could not extract user ID. Selected member:', selectedMember);
        toast.error(t('errors.invalidData') || 'Invalid member data. Please try again.');
        return;
      }

      // Prepare update data (exclude email if present)
      const { email, ...updateData } = data;
      const finalUpdateData = {
        role: updateData.role,
        permissions: updateData.permissions
      };

      console.log('Calling updateMemberRole with:', { projectId: id, userId: memberUserId, data: finalUpdateData });

      await projectService.updateMemberRole(id, memberUserId, finalUpdateData);

      toast.success(t('success.updated'));
      setShowEditMemberModal(false);
      setSelectedMember(null);
      resetMemberEdit();
      fetchProject();
    } catch (error) {
      console.error('Update member error:', error);
      console.error('Error response:', error.response);
      toast.error(error.response?.data?.message || error.message || t('errors.serverError'));
    }
  };

  const onMilestoneSubmit = async (data) => {
    try {
      await projectService.addMilestone(id, data);
      toast.success(t('success.created'));
      setShowMilestoneModal(false);
      resetMilestone();
      fetchProject();
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setShowFileUploadModal(true);
    // Reset the input so the same file can be selected again
    e.target.value = '';
  };

  const onFileSubmit = async (data) => {
    if (!selectedFile) return;

    try {
      await projectService.uploadFile(id, selectedFile, data.category || 'document', data.isClientVisible || false, data.name || selectedFile.name);
      toast.success(t('success.created'));
      setShowFileUploadModal(false);
      setSelectedFile(null);
      resetFile();
      fetchProject();
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    }
  };

  const handleDeleteFile = async (fileId, fileName) => {
    const confirmed = await confirm({
      title: t('common.delete'),
      message: `Are you sure you want to delete "${fileName}"? This action cannot be undone.`,
      confirmText: t('common.delete'),
      variant: 'danger'
    });

    if (confirmed) {
      try {
        await projectService.deleteFile(id, fileId);
        toast.success(t('success.deleted'));
        fetchProject();
      } catch (error) {
        toast.error(error.response?.data?.message || t('errors.serverError'));
      }
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  if (!project) {
    return null;
  }

  // Helper function to check if current user has a specific permission
  const hasPermission = (permission) => {
    if (!currentUser || !project) return false;

    // Owner always has all permissions
    if (project.owner?._id?.toString() === currentUser._id?.toString()) {
      return true;
    }

    // Check member permissions
    const member = project.members?.find(m =>
      m.user?._id?.toString() === currentUser._id?.toString() ||
      m.user?.toString() === currentUser._id?.toString()
    );

    if (!member) return false;

    // Check the actual permission
    return member.permissions?.[permission] === true;
  };

  // Combine owner and members, removing duplicates (owner might be in members array)
  const ownerId = project.owner?._id?.toString();
  const uniqueMembers = project.members
    .map(m => m.user)
    .filter(member => member && member._id?.toString() !== ownerId);
  const allMembers = [project.owner, ...uniqueMembers].filter(Boolean);
  const completedTasks = tasks.filter(t => t.status === 'completed').length;
  const inProgressTasks = tasks.filter(t => t.status === 'in_progress').length;
  const overdueTasks = tasks.filter(t => {
    if (!t.dueDate) return false;
    return dayjs(t.dueDate).isBefore(dayjs(), 'day') && t.status !== 'completed';
  }).length;

  const completedMilestones = project.milestones?.filter(m => m.status === 'completed').length || 0;
  const totalMilestones = project.milestones?.length || 0;

  const daysRemaining = project.endDate ? dayjs(project.endDate).diff(dayjs(), 'day') : null;
  const daysElapsed = project.startDate ? dayjs().diff(dayjs(project.startDate), 'day') : null;
  const totalDays = project.startDate && project.endDate
    ? dayjs(project.endDate).diff(dayjs(project.startDate), 'day')
    : null;

  const budgetUtilization = project.budget > 0
    ? ((project.cost || 0) / project.budget) * 100
    : 0;

  const stats = [
    {
      label: t('common.progress'),
      value: `${project.progress}%`,
      icon: ChartBarIcon,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
      subtitle: `${completedTasks}/${tasks.length} tasks completed`
    },
    // Only show budget if user has permission to view financials
    ...(hasPermission('canViewFinancials') && project.budget !== undefined ? [{
      label: t('projects.budget'),
      value: `${project.currency || 'EGP'} ${(project.budget || 0).toLocaleString()}`,
      icon: CurrencyDollarIcon,
      color: 'text-green-500',
      bgColor: 'bg-green-50',
      subtitle: `${budgetUtilization.toFixed(1)}% ${t('projects.utilized')}`
    }] : []),
    {
      label: t('projects.team'),
      value: `${allMembers.length} ${allMembers.length === 1 ? t('projects.member') : t('projects.members')}`,
      icon: UserGroupIcon,
      color: 'text-purple-500',
      bgColor: 'bg-purple-50',
      subtitle: `${project.members.length} ${t('projects.teamMembers')}`
    },
    {
      label: tasks.length > 0 ? t('tasks.title') : t('projects.noTasks'),
      value: tasks.length > 0 ? `${completedTasks}/${tasks.length}` : '0',
      icon: ListBulletIcon,
      color: 'text-orange-500',
      bgColor: 'bg-orange-50',
      subtitle: `${inProgressTasks} in progress`
    },
    {
      label: t('projects.milestones'),
      value: `${completedMilestones}/${totalMilestones}`,
      icon: TrophyIcon,
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-50',
      subtitle: totalMilestones > 0 ? `${Math.round((completedMilestones / totalMilestones) * 100)}% complete` : 'No milestones'
    },
    {
      label: project.endDate ? t('projects.daysRemaining') : t('projects.noEndDate'),
      value: daysRemaining !== null ? (daysRemaining > 0 ? `${daysRemaining}` : t('tasks.overdue')) : '-',
      icon: CalendarDaysIcon,
      color: daysRemaining !== null && daysRemaining < 7 ? 'text-red-500' : 'text-indigo-500',
      bgColor: daysRemaining !== null && daysRemaining < 7 ? 'bg-red-50' : 'bg-indigo-50',
      subtitle: project.endDate ? dayjs(project.endDate).format('MMM D, YYYY') : t('common.notSet')
    }
  ];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'created': return PlusIcon;
      case 'updated': return PencilIcon;
      case 'completed': return CheckCircleIcon;
      case 'comment': return ChatBubbleLeftRightIcon;
      case 'file_uploaded': return DocumentIcon;
      case 'member_added': return UserGroupIcon;
      default: return ArrowPathIcon;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'created': return 'text-green-500 bg-green-50';
      case 'updated': return 'text-blue-500 bg-blue-50';
      case 'completed': return 'text-purple-500 bg-purple-50';
      case 'comment': return 'text-orange-500 bg-orange-50';
      case 'file_uploaded': return 'text-indigo-500 bg-indigo-50';
      case 'member_added': return 'text-pink-500 bg-pink-50';
      default: return 'text-gray-500 bg-gray-50';
    }
  };

  return (
    <>
      <Helmet>
        <title>{project.name} - ProCTM</title>
      </Helmet>

      <PageHeader
        title={project.name}
        backUrl="/projects"
        actions={
          hasPermission('canEditProject') && (
            <div className="flex items-center gap-2">
              <Button variant="outline" icon={PencilIcon} onClick={() => navigate(`/projects/${id}/edit`)}>
                {t('common.edit')}
              </Button>
              {project.owner?._id?.toString() === currentUser?._id?.toString() && (
                <Button variant="danger" icon={TrashIcon} onClick={handleDelete}>
                  {t('common.delete')}
                </Button>
              )}
            </div>
          )
        }
      />

      {/* Hero Section */}
      <Card className="mb-6">
        <CardBody>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <h1 className="text-3xl font-bold text-neutral-dark truncate">{project.name}</h1>
                <StatusBadge status={project.status} />
                <PriorityBadge priority={project.priority} />
                {project.category && (
                  <Badge variant="secondary">{project.category}</Badge>
                )}
              </div>

              {project.description && (
                <p className="text-gray-600 mb-4 line-clamp-2">{project.description}</p>
              )}

              <div className="flex flex-wrap items-center gap-6 text-sm">
                {project.client && (
                  <div className="flex items-center gap-2">
                    <BuildingOfficeIcon className="w-4 h-4 text-gray-400" />
                    <Link to={`/clients/${project.client._id}`} className="text-primary hover:underline font-medium">
                      {project.client.name}
                    </Link>
                  </div>
                )}
                {project.startDate && (
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">
                      Started: {dayjs(project.startDate).format('MMM D, YYYY')}
                    </span>
                  </div>
                )}
                {project.createdAt && (
                  <div className="flex items-center gap-2">
                    <ClockIcon className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">
                      Created {dayjs(project.createdAt).fromNow()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex-shrink-0">
              <div className="p-4 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl border border-primary/20">
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-2">{t('common.progress')}</p>
                  <div className="text-4xl font-bold text-primary mb-2">{project.progress}%</div>
                  <ProgressBar value={project.progress} color="primary" size="lg" />
                </div>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        {stats.map((stat, index) => (
          <Card key={index} className="hover:shadow-md transition-shadow">
            <CardBody>
              <div className="flex items-center justify-between mb-3">
                <div className={`${stat.bgColor} p-2.5 rounded-lg`}>
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                </div>
              </div>
              <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
              <p className={`text-xl font-bold ${stat.color} mb-1`}>{stat.value}</p>
              {stat.subtitle && (
                <p className="text-xs text-gray-400">{stat.subtitle}</p>
              )}
            </CardBody>
          </Card>
        ))}
      </div>

      {/* Main Content Tabs */}
      <Card>
        <CardBody>
          <Tabs>
            <TabList className="mb-6">
              <Tab active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
                {t('common.overview')}
              </Tab>
              <Tab active={activeTab === 'tasks'} onClick={() => setActiveTab('tasks')}>
                {t('tasks.title')} ({tasks.length})
              </Tab>
              <Tab active={activeTab === 'milestones'} onClick={() => setActiveTab('milestones')}>
                {t('projects.milestones')} ({totalMilestones})
              </Tab>
              <Tab active={activeTab === 'files'} onClick={() => setActiveTab('files')}>
                {t('projects.files')} ({project.files?.length || 0})
              </Tab>
              <Tab active={activeTab === 'notes'} onClick={() => setActiveTab('notes')}>
                {t('common.notes')} ({project.projectNotes?.length || 0})
              </Tab>
              <Tab active={activeTab === 'activities'} onClick={() => setActiveTab('activities')}>
                {t('projects.timeline')} ({project.activities?.length || 0})
              </Tab>
            </TabList>

            <TabPanels>
              {/* Overview Tab */}
              <TabPanel active={activeTab === 'overview'}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Project Information */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <DocumentIcon className="w-5 h-5 text-primary" />
                        {t('projects.projectInformation')}
                      </CardTitle>
                    </CardHeader>
                    <CardBody>
                      <div className="space-y-4">
                        {project.client && (
                          <div className="flex items-center justify-between py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-500 flex items-center gap-2">
                              <BuildingOfficeIcon className="w-4 h-4" />
                              {t('projects.client')}
                            </span>
                            <Link to={`/clients/${project.client._id}`} className="text-primary hover:underline font-medium">
                              {project.client.name}
                            </Link>
                          </div>
                        )}
                        <div className="flex items-center justify-between py-2 border-b border-gray-100">
                          <span className="text-sm text-gray-500 flex items-center gap-2">
                            <UserIcon className="w-4 h-4" />
                            {t('projects.owner')}
                          </span>
                          <div className="flex items-center gap-2">
                            <Avatar
                              src={project.owner?.avatar ? (project.owner.avatar.startsWith('http') ? project.owner.avatar : process.env.REACT_APP_SOCKET_URL + project.owner.avatar) : null}
                              name={`${project.owner?.firstName} ${project.owner?.lastName}`}
                              size="xs"
                            />
                            <span className="font-medium">
                              {project.owner?.firstName} {project.owner?.lastName}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between py-2 border-b border-gray-100">
                          <span className="text-sm text-gray-500 flex items-center gap-2">
                            <FlagIcon className="w-4 h-4" />
                            {t('common.status')}
                          </span>
                          <StatusBadge status={project.status} />
                        </div>
                        <div className="flex items-center justify-between py-2 border-b border-gray-100">
                          <span className="text-sm text-gray-500 flex items-center gap-2">
                            <FireIcon className="w-4 h-4" />
                            {t('common.priority')}
                          </span>
                          <PriorityBadge priority={project.priority} />
                        </div>
                        {project.category && (
                          <div className="flex items-center justify-between py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-500 flex items-center gap-2">
                              <TagIcon className="w-4 h-4" />
                              Category
                            </span>
                            <Badge variant="secondary">{project.category}</Badge>
                          </div>
                        )}
                      </div>
                    </CardBody>
                  </Card>

                  {/* Timeline & Dates */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <CalendarDaysIcon className="w-5 h-5 text-primary" />
                        {t('projects.timelineAndDates')}
                      </CardTitle>
                    </CardHeader>
                    <CardBody>
                      <div className="space-y-4">
                        {project.startDate && (
                          <div className="flex items-center justify-between py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-500">{t('projects.startDate')}</span>
                            <span className="font-medium">{dayjs(project.startDate).format('MMM D, YYYY')}</span>
                          </div>
                        )}
                        {project.endDate && (
                          <div className="flex items-center justify-between py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-500">{t('projects.endDate')}</span>
                            <div className="flex items-center gap-2">
                              <span className={`font-medium ${daysRemaining !== null && daysRemaining < 0 ? 'text-red-600' : ''}`}>
                                {dayjs(project.endDate).format('MMM D, YYYY')}
                              </span>
                              {daysRemaining !== null && daysRemaining < 0 && (
                                <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />
                              )}
                            </div>
                          </div>
                        )}
                        {project.actualEndDate && (
                          <div className="flex items-center justify-between py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-500">Actual End Date</span>
                            <span className="font-medium">{dayjs(project.actualEndDate).format('MMM D, YYYY')}</span>
                          </div>
                        )}
                        {totalDays !== null && (
                          <div className="flex items-center justify-between py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-500">{t('projects.duration')}</span>
                            <span className="font-medium">{totalDays} {t('projects.days')}</span>
                          </div>
                        )}
                        {daysElapsed !== null && (
                          <div className="flex items-center justify-between py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-500">{t('projects.daysElapsed')}</span>
                            <span className="font-medium">{daysElapsed} {t('projects.days')}</span>
                          </div>
                        )}
                        {project.createdAt && (
                          <div className="flex items-center justify-between py-2">
                            <span className="text-sm text-gray-500">{t('common.createdAt')}</span>
                            <span className="font-medium">{dayjs(project.createdAt).format('MMM D, YYYY')}</span>
                          </div>
                        )}
                      </div>
                    </CardBody>
                  </Card>

                  {/* Financial Information */}
                  {hasPermission('canViewFinancials') && project.budget !== undefined && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <CurrencyDollarIcon className="w-5 h-5 text-primary" />
                          {t('projects.financialInformation')}
                        </CardTitle>
                      </CardHeader>
                      <CardBody>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-500">{t('projects.currency')}</span>
                            <span className="font-medium">{project.currency || 'EGP'}</span>
                          </div>
                          <div className="flex items-center justify-between py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-500">{t('projects.budget')}</span>
                            <span className="text-lg font-bold text-green-600">
                              {project.currency || 'EGP'} {(project.budget || 0).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex items-center justify-between py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-500">{t('projects.cost')}</span>
                            <span className="text-lg font-bold text-orange-600">
                              {project.currency || 'EGP'} {(project.cost || 0).toLocaleString()}
                            </span>
                          </div>
                          {project.budget > 0 && (
                            <div className="pt-2">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm text-gray-500">{t('projects.budgetUtilization')}</span>
                                <span className={`text-sm font-medium ${budgetUtilization > 100 ? 'text-red-600' : budgetUtilization > 80 ? 'text-orange-600' : 'text-green-600'}`}>
                                  {budgetUtilization.toFixed(1)}%
                                </span>
                              </div>
                              <ProgressBar
                                value={Math.min(100, budgetUtilization)}
                                color={budgetUtilization > 100 ? 'danger' : budgetUtilization > 80 ? 'accent' : 'success'}
                                size="md"
                              />
                              {project.budget > 0 && (
                                <div className="mt-2 text-xs text-gray-500">
                                  {t('projects.remaining')}: {project.currency || 'EGP'} {Math.max(0, project.budget - (project.cost || 0)).toLocaleString()}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  )}

                  {/* Team Members */}
                  <Card>
                    <CardHeader className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <UserGroupIcon className="w-5 h-5 text-primary" />
                        {t('projects.team')} ({allMembers.length})
                      </CardTitle>
                      {(project.owner?._id?.toString() === currentUser?._id?.toString() ||
                        project.members?.some(m => m.user?._id?.toString() === currentUser?._id?.toString() && m.permissions?.canManageMembers)) && (
                          <Button
                            size="sm"
                            icon={PlusIcon}
                            onClick={() => {
                              resetMemberAdd();
                              setShowAddMemberModal(true);
                            }}
                          >
                            {t('projects.addMember') || 'Add Member'}
                          </Button>
                        )}
                    </CardHeader>
                    <CardBody>
                      <div className="space-y-3">
                        {allMembers.map((member) => {
                          const isOwner = member._id?.toString() === ownerId;
                          const memberData = project.members.find(m => m.user?._id?.toString() === member._id?.toString());
                          const role = isOwner ? t('projects.owner') : (memberData?.role || t('projects.member'));
                          const permissions = isOwner ? {
                            canEditProject: true,
                            canManageFiles: true,
                            canManageNotes: true,
                            canManageTasks: true,
                            canManageMilestones: true,
                            canManageMembers: true,
                            canViewFinancials: true,
                            canChat: true
                          } : (memberData?.permissions || {});

                          return (
                            <div key={member._id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-primary transition-colors">
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <Avatar
                                  src={member.avatar ? (member.avatar.startsWith('http') ? member.avatar : process.env.REACT_APP_SOCKET_URL + member.avatar) : null}
                                  name={`${member.firstName} ${member.lastName}`}
                                  size="sm"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <p className="font-medium text-sm">
                                      {member.firstName} {member.lastName}
                                    </p>
                                    <Badge variant="neutral" className="text-xs">
                                      {role}
                                    </Badge>
                                  </div>
                                  {member.email && (
                                    <div className="flex items-center gap-1 text-xs text-gray-500">
                                      <EnvelopeIcon className="w-3 h-3" />
                                      <span className="truncate">{member.email}</span>
                                    </div>
                                  )}
                                  {!isOwner && memberData && (
                                    <div className="flex flex-wrap gap-1 mt-2">
                                      {Object.entries(permissions).map(([key, value]) => {
                                        if (key === 'canChat') return null; // Skip chat permission in display
                                        const permissionLabels = {
                                          canEditProject: t('projects.permissions.editProject') || 'Edit Project',
                                          canManageFiles: t('projects.permissions.manageFiles') || 'Manage Files',
                                          canManageNotes: t('projects.permissions.manageNotes') || 'Manage Notes',
                                          canManageTasks: t('projects.permissions.manageTasks') || 'Manage Tasks',
                                          canManageMilestones: t('projects.permissions.manageMilestones') || 'Manage Milestones',
                                          canManageMembers: t('projects.permissions.manageMembers') || 'Manage Members',
                                          canViewFinancials: t('projects.permissions.viewFinancials') || 'View Financials'
                                        };
                                        return value ? (
                                          <Badge key={key} variant="success" className="text-xs">
                                            <ShieldCheckIcon className="w-3 h-3 mr-1" />
                                            {permissionLabels[key]}
                                          </Badge>
                                        ) : null;
                                      })}
                                    </div>
                                  )}
                                </div>
                              </div>
                              {!isOwner && hasPermission('canManageMembers') && (
                                <div className="flex items-center gap-2">
                                  <Button
                                    size="xs"
                                    variant="outline"
                                    icon={PencilIcon}
                                    onClick={() => {
                                      console.log('Edit button clicked');
                                      console.log('memberData:', memberData);
                                      console.log('member:', member);

                                      // Store both the memberData (from project.members) and the user object
                                      // memberData.user is the ObjectId, member is the populated user
                                      const userId = member?._id || memberData?.user?._id || memberData?.user;
                                      console.log('Extracted userId:', userId);

                                      const memberToEdit = {
                                        ...memberData,
                                        user: member, // Full user object with _id
                                        userId: userId ? (typeof userId === 'string' ? userId : userId.toString()) : null
                                      };

                                      console.log('memberToEdit:', memberToEdit);
                                      setSelectedMember(memberToEdit);

                                      const currentPermissions = memberData?.permissions || getRolePermissions(memberData?.role || 'contributor');
                                      console.log('Setting form with:', {
                                        role: memberData?.role || 'contributor',
                                        permissions: currentPermissions
                                      });

                                      resetMemberEdit({
                                        role: memberData?.role || 'contributor',
                                        permissions: currentPermissions
                                      });
                                      setShowEditMemberModal(true);
                                    }}
                                    title={t('common.edit')}
                                  />
                                  <Button
                                    size="xs"
                                    variant="danger"
                                    icon={TrashIcon}
                                    onClick={async () => {
                                      const confirmed = await confirm({
                                        title: t('common.remove'),
                                        message: t('projects.confirmRemoveMember') || `Are you sure you want to remove ${member.firstName} ${member.lastName} from this project?`,
                                        confirmText: t('common.remove'),
                                        variant: 'danger'
                                      });
                                      if (confirmed) {
                                        try {
                                          await projectService.removeMember(id, member._id);
                                          toast.success(t('success.updated'));
                                          fetchProject();
                                        } catch (error) {
                                          toast.error(error.response?.data?.message || t('errors.serverError'));
                                        }
                                      }
                                    }}
                                    title={t('common.remove')}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </CardBody>
                  </Card>

                  {/* Tags */}
                  {project.tags && project.tags.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <TagIcon className="w-5 h-5 text-primary" />
                          {t('common.tags')}
                        </CardTitle>
                      </CardHeader>
                      <CardBody>
                        <div className="flex flex-wrap gap-2">
                          {project.tags.map((tag, index) => (
                            <Badge key={index} variant="neutral">{tag}</Badge>
                          ))}
                        </div>
                      </CardBody>
                    </Card>
                  )}

                  {/* Task Summary */}
                  {tasks.length > 0 && (
                    <Card className="lg:col-span-2">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <ListBulletIcon className="w-5 h-5 text-primary" />
                          Task Summary
                        </CardTitle>
                      </CardHeader>
                      <CardBody>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="p-4 bg-blue-50 rounded-lg">
                            <p className="text-xs text-gray-500 mb-1">{t('projects.totalTasks')}</p>
                            <p className="text-2xl font-bold text-blue-600">{tasks.length}</p>
                          </div>
                          <div className="p-4 bg-green-50 rounded-lg">
                            <p className="text-xs text-gray-500 mb-1">Completed</p>
                            <p className="text-2xl font-bold text-green-600">{completedTasks}</p>
                          </div>
                          <div className="p-4 bg-orange-50 rounded-lg">
                            <p className="text-xs text-gray-500 mb-1">{t('projects.inProgress')}</p>
                            <p className="text-2xl font-bold text-orange-600">{inProgressTasks}</p>
                          </div>
                          <div className="p-4 bg-red-50 rounded-lg">
                            <p className="text-xs text-gray-500 mb-1">Overdue</p>
                            <p className="text-2xl font-bold text-red-600">{overdueTasks}</p>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  )}
                </div>
              </TabPanel>

              {/* Tasks Tab */}
              <TabPanel active={activeTab === 'tasks'}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">{t('tasks.title')}</h3>
                  {hasPermission('canManageTasks') && (
                    <Button size="sm" icon={PlusIcon} onClick={() => setShowTaskModal(true)}>
                      {t('tasks.newTask')}
                    </Button>
                  )}
                </div>

                {tasks.length > 0 ? (
                  <div className="space-y-3">
                    {tasks.map((task) => (
                      <Link
                        key={task._id}
                        to={`/tasks/${task._id}`}
                        className="block p-4 bg-gray-50 rounded-lg hover:bg-gray-100 border border-gray-200 hover:border-primary transition-all"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-1">
                            <div className={`w-1 h-12 rounded-full ${task.status === 'completed' ? 'bg-success' :
                              task.status === 'in_progress' ? 'bg-accent' :
                                task.status === 'review' ? 'bg-secondary' :
                                  'bg-primary'
                              }`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <p className={`font-semibold ${task.status === 'completed' ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                                  {task.title}
                                </p>
                                <StatusBadge status={task.status} />
                                {task.priority && <PriorityBadge priority={task.priority} />}
                              </div>
                              {task.description && (
                                <p className="text-sm text-gray-600 line-clamp-1 mb-2">{task.description}</p>
                              )}
                              <div className="flex items-center gap-4 text-xs text-gray-500">
                                {task.dueDate && (
                                  <div className="flex items-center gap-1">
                                    <CalendarIcon className="w-3 h-3" />
                                    <span className={dayjs(task.dueDate).isBefore(dayjs(), 'day') && task.status !== 'completed' ? 'text-red-600 font-medium' : ''}>
                                      {t('projects.due')}: {dayjs(task.dueDate).format('MMM D, YYYY')}
                                    </span>
                                  </div>
                                )}
                                {task.assignees && task.assignees.length > 0 && (
                                  <div className="flex items-center gap-1">
                                    <UserGroupIcon className="w-3 h-3" />
                                    <span>{task.assignees.length} assignee{task.assignees.length > 1 ? 's' : ''}</span>
                                  </div>
                                )}
                                {task.progress !== undefined && (
                                  <div className="flex items-center gap-2">
                                    <span>{t('common.progress')}: {task.progress}%</span>
                                    <ProgressBar value={task.progress} size="sm" className="w-20" />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {task.assignees && task.assignees.length > 0 && (
                              <AvatarGroup users={task.assignees} max={3} size="xs" />
                            )}
                            <ArrowRightIcon className="w-5 h-5 text-gray-400" />
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    title={t('tasks.noTasks')}
                    {...(hasPermission('canManageTasks') && {
                      action: () => setShowTaskModal(true),
                      actionLabel: t('tasks.newTask')
                    })}
                  />
                )}
              </TabPanel>

              {/* Milestones Tab */}
              <TabPanel active={activeTab === 'milestones'}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Milestones</h3>
                  {hasPermission('canManageMilestones') && (
                    <Button size="sm" icon={PlusIcon} onClick={() => setShowMilestoneModal(true)}>
                      {t('projects.addMilestone')}
                    </Button>
                  )}
                </div>

                {project.milestones && project.milestones.length > 0 ? (
                  <div className="space-y-4">
                    {project.milestones
                      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
                      .map((milestone) => (
                        <div
                          key={milestone._id}
                          className={`p-5 rounded-lg border-l-4 ${milestone.status === 'completed'
                            ? 'bg-green-50 border-green-500'
                            : dayjs(milestone.dueDate).isBefore(dayjs(), 'day')
                              ? 'bg-red-50 border-red-500'
                              : 'bg-gray-50 border-primary'
                            }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h4 className="font-semibold text-lg">{milestone.title}</h4>
                                <StatusBadge status={milestone.status} />
                                {milestone.status === 'completed' && (
                                  <CheckCircleIconSolid className="w-5 h-5 text-green-500" />
                                )}
                              </div>
                              {milestone.description && (
                                <p className="text-sm text-gray-600 mb-3">{milestone.description}</p>
                              )}
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <div className="flex items-center gap-1">
                                  <CalendarIcon className="w-4 h-4" />
                                  <span>{t('projects.due')}: {dayjs(milestone.dueDate).format('MMM D, YYYY')}</span>
                                </div>
                                {milestone.completedAt && (
                                  <div className="flex items-center gap-1">
                                    <CheckCircleIcon className="w-4 h-4" />
                                    <span>{t('projects.completed')}: {dayjs(milestone.completedAt).format('MMM D, YYYY')}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <EmptyState
                    title="No milestones yet"
                    description="Create milestones to track major project goals"
                    {...(hasPermission('canManageMilestones') && {
                      action: () => setShowMilestoneModal(true),
                      actionLabel: t('projects.addMilestone')
                    })}
                  />
                )}
              </TabPanel>

              {/* Files Tab */}
              <TabPanel active={activeTab === 'files'}>
                <div className="flex flex-col sm:flex-row gap-4 mb-4">
                  {/* Search Input */}
                  <div className="flex-1 relative">
                    <div className="absolute inset-y-0 left-0 rtl:left-auto rtl:right-0 flex items-center pl-3 rtl:pl-0 rtl:pr-3 pointer-events-none">
                      <MagnifyingGlassIcon className="w-5 h-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder={t('common.search') + ' files...'}
                      value={fileSearchQuery}
                      onChange={(e) => setFileSearchQuery(e.target.value)}
                      className="input pl-10 rtl:pl-3 rtl:pr-10 w-full"
                    />
                    {fileSearchQuery && (
                      <button
                        onClick={() => setFileSearchQuery('')}
                        className="absolute inset-y-0 right-0 rtl:right-auto rtl:left-0 flex items-center pr-3 rtl:pr-0 rtl:pl-3"
                      >
                        <XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                      </button>
                    )}
                  </div>
                  {hasPermission('canManageFiles') && (
                    <label className="btn btn-primary btn-sm cursor-pointer whitespace-nowrap">
                      <PlusIcon className="w-4 h-4 mr-2" />
                      {t('projects.uploadFile')}
                      <input
                        type="file"
                        className="hidden"
                        onChange={handleFileSelect}
                      />
                    </label>
                  )}
                </div>

                {project.files && project.files.length > 0 ? (
                  (() => {
                    const filteredFiles = project.files.filter(file => {
                      if (!fileSearchQuery) return true;
                      const query = fileSearchQuery.toLowerCase();
                      const fileName = (file.name || file.originalName || '').toLowerCase();
                      const originalName = (file.originalName || '').toLowerCase();
                      const category = (file.category || '').toLowerCase();
                      const uploaderName = file.uploadedBy
                        ? `${file.uploadedBy.firstName} ${file.uploadedBy.lastName}`.toLowerCase()
                        : '';
                      return fileName.includes(query) ||
                        originalName.includes(query) ||
                        category.includes(query) ||
                        uploaderName.includes(query);
                    });

                    return filteredFiles.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredFiles.map((file) => (
                          <div
                            key={file._id}
                            className="group relative p-4 bg-gray-50 rounded-lg hover:bg-gray-100 border border-gray-200 hover:border-primary transition-all"
                          >
                            {/* Delete Button */}
                            {hasPermission('canManageFiles') && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteFile(file._id, file.name || file.originalName);
                                }}
                                className="absolute top-2 right-2 rtl:right-auto rtl:left-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 bg-red-50 hover:bg-red-100 rounded text-red-600 hover:text-red-700"
                                title={t('common.delete')}
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            )}

                            <a
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block"
                            >
                              <div className="flex items-start gap-3">
                                <div className="p-2 bg-primary/10 rounded-lg">
                                  <DocumentIcon className="w-6 h-6 text-primary" />
                                </div>
                                <div className="flex-1 min-w-0 pr-8">
                                  <p className="font-medium text-sm truncate mb-1">{file.name || file.originalName}</p>
                                  {file.name !== file.originalName && (
                                    <p className="text-xs text-gray-400 truncate">{file.originalName}</p>
                                  )}
                                  <div className="flex items-center gap-3 text-xs text-gray-500">
                                    <span>{(file.size / 1024).toFixed(1)} KB</span>
                                    {file.uploadedBy && (
                                      <span>by {file.uploadedBy.firstName} {file.uploadedBy.lastName}</span>
                                    )}
                                    {file.uploadedAt && (
                                      <span>{dayjs(file.uploadedAt).format('MMM D')}</span>
                                    )}
                                  </div>
                                  {file.category && (
                                    <Badge variant="secondary" className="mt-2 text-xs">{file.category}</Badge>
                                  )}
                                </div>
                              </div>
                            </a>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <EmptyState title={t('common.noResults')} />
                    );
                  })()
                ) : (
                  <EmptyState title={t('projects.noFilesYet')} />
                )}
              </TabPanel>

              {/* Notes Tab */}
              <TabPanel active={activeTab === 'notes'}>
                {hasPermission('canManageNotes') && (
                  <div className="flex justify-end mb-4">
                    <Button size="sm" icon={PlusIcon} onClick={() => setShowNoteModal(true)}>
                      {t('projects.addNote')}
                    </Button>
                  </div>
                )}

                {project.projectNotes && project.projectNotes.length > 0 ? (
                  <div className="space-y-4">
                    {project.projectNotes.slice().reverse().map((note) => (
                      <div key={note._id} className="p-5 bg-gray-50 rounded-lg border-l-4 border-primary">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <Avatar
                              src={note.createdBy?.avatar ? (note.createdBy.avatar.startsWith('http') ? note.createdBy.avatar : process.env.REACT_APP_SOCKET_URL + note.createdBy.avatar) : null}
                              name={`${note.createdBy?.firstName} ${note.createdBy?.lastName}`}
                              size="sm"
                            />
                            <div>
                              <p className="font-medium text-sm">
                                {note.createdBy?.firstName} {note.createdBy?.lastName}
                              </p>
                              <p className="text-xs text-gray-500">
                                {dayjs(note.createdAt).format('MMM D, YYYY h:mm A')}
                                {note.updatedAt && note.updatedAt !== note.createdAt && (
                                  <span className="ml-2">(edited {dayjs(note.updatedAt).fromNow()})</span>
                                )}
                              </p>
                            </div>
                          </div>
                          {note.isInternal && (
                            <Badge variant="accent" className="text-xs">{t('projects.internal')}</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.content}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    title="No notes yet"
                    {...(hasPermission('canManageNotes') && {
                      action: () => setShowNoteModal(true),
                      actionLabel: t('projects.addNote')
                    })}
                  />
                )}
              </TabPanel>

              {/* Activities/Timeline Tab */}
              <TabPanel active={activeTab === 'activities'}>
                <h3 className="text-lg font-semibold mb-4">{t('projects.projectTimeline')}</h3>

                {project.activities && project.activities.length > 0 ? (
                  <div className="space-y-4">
                    {project.activities
                      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                      .map((activity, index) => {
                        const ActivityIcon = getActivityIcon(activity.type);
                        const iconColor = getActivityColor(activity.type);

                        return (
                          <div key={activity._id || index} className="flex gap-4">
                            <div className="flex-shrink-0">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${iconColor}`}>
                                <ActivityIcon className="w-5 h-5" />
                              </div>
                              {index < project.activities.length - 1 && (
                                <div className="w-0.5 h-full bg-gray-200 ml-5 -mt-2" />
                              )}
                            </div>
                            <div className="flex-1 pb-6">
                              <div className="flex items-center gap-2 mb-1">
                                {activity.user && (
                                  <Avatar
                                    src={activity.user.avatar ? (activity.user.avatar.startsWith('http') ? activity.user.avatar : process.env.REACT_APP_SOCKET_URL + activity.user.avatar) : null}
                                    name={`${activity.user.firstName} ${activity.user.lastName}`}
                                    size="xs"
                                  />
                                )}
                                <span className="font-medium text-sm">
                                  {activity.user ? `${activity.user.firstName} ${activity.user.lastName}` : 'System'}
                                </span>
                                <span className="text-xs text-gray-400">
                                  {dayjs(activity.timestamp).fromNow()}
                                </span>
                              </div>
                              <p className="text-sm text-gray-700 mb-1">{activity.description}</p>
                              <p className="text-xs text-gray-500">
                                {dayjs(activity.timestamp).format('MMM D, YYYY h:mm A')}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <EmptyState title="No activities yet" />
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </CardBody>
      </Card>

      {/* Add Task Modal */}
      <Modal isOpen={showTaskModal} onClose={() => setShowTaskModal(false)} title={t('tasks.newTask')}>
        <form onSubmit={handleTaskSubmit(onTaskSubmit)}>
          <ModalBody>
            <div className="space-y-4">
              <Input
                label={t('common.name')}
                {...registerTask('title', { required: true })}
              />
              <Textarea
                label={t('common.description')}
                rows={3}
                {...registerTask('description')}
              />
              <Input
                label={t('tasks.dueDate')}
                type="date"
                {...registerTask('dueDate')}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" onClick={() => setShowTaskModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit">{t('common.create')}</Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Add Note Modal */}
      <Modal isOpen={showNoteModal} onClose={() => setShowNoteModal(false)} title={t('projects.addNote')}>
        <form onSubmit={handleNoteSubmit(onNoteSubmit)}>
          <ModalBody>
            <Textarea
              label="Note"
              rows={6}
              {...registerNote('content', { required: true })}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" onClick={() => setShowNoteModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit">{t('common.add')}</Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Add Milestone Modal */}
      <Modal isOpen={showMilestoneModal} onClose={() => setShowMilestoneModal(false)} title={t('projects.addMilestone')}>
        <form onSubmit={handleMilestoneSubmit(onMilestoneSubmit)}>
          <ModalBody>
            <div className="space-y-4">
              <Input
                label={t('common.name')}
                {...registerMilestone('title', { required: true })}
              />
              <Textarea
                label={t('common.description')}
                rows={3}
                {...registerMilestone('description')}
              />
              <Input
                label={t('tasks.dueDate')}
                type="date"
                {...registerMilestone('dueDate', { required: true })}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" onClick={() => setShowMilestoneModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit">{t('common.add')}</Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Upload File Modal */}
      <Modal isOpen={showFileUploadModal} onClose={() => { setShowFileUploadModal(false); setSelectedFile(null); resetFile(); }} title={t('projects.uploadFile')}>
        <form onSubmit={handleFileSubmit(onFileSubmit)}>
          <ModalBody>
            <div className="space-y-4">
              {selectedFile && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-1">{t('common.name')}: {selectedFile.name}</p>
                  <p className="text-xs text-gray-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                </div>
              )}
              <Input
                label={t('projects.fileName')}
                placeholder={selectedFile ? selectedFile.name : t('projects.fileName')}
                {...registerFile('name')}
                helpText={t('projects.leaveEmptyForOriginal')}
              />
              <Controller
                name="category"
                control={controlFile}
                defaultValue="document"
                render={({ field }) => (
                  <Select
                    label={t('projects.fileCategory')}
                    options={[
                      { value: 'document', label: t('projects.category') },
                      { value: 'image', label: 'Image' },
                      { value: 'proposal', label: 'Proposal' },
                      { value: 'contract', label: 'Contract' },
                      { value: 'invoice', label: 'Invoice' },
                      { value: 'other', label: 'Other' }
                    ]}
                    {...field}
                  />
                )}
              />
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isClientVisible"
                  {...registerFile('isClientVisible')}
                  className="rounded"
                />
                <label htmlFor="isClientVisible" className="text-sm text-gray-700">
                  {t('projects.makeVisibleToClient')}
                </label>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" onClick={() => { setShowFileUploadModal(false); setSelectedFile(null); resetFile(); }}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={!selectedFile}>
              {t('projects.uploadFile')}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Add Member Modal */}
      <Modal isOpen={showAddMemberModal} onClose={() => { setShowAddMemberModal(false); resetMemberAdd(); }} title={t('projects.addMember') || 'Add Team Member'}>
        <form onSubmit={handleMemberSubmitAdd(onAddMember)}>
          <ModalBody>
            <div className="space-y-4">
              <Input
                label={t('common.email')}
                type="email"
                placeholder={t('projects.memberEmailPlaceholder') || 'Enter member email address'}
                {...registerMemberAdd('email', { required: t('errors.required') })}
              />

              <Controller
                name="role"
                control={controlMemberAdd}
                render={({ field }) => (
                  <Select
                    label={t('projects.role') || 'Role'}
                    options={[
                      { value: 'manager', label: t('projects.roles.manager') || 'Manager' },
                      { value: 'contributor', label: t('projects.roles.contributor') || 'Contributor' },
                      { value: 'viewer', label: t('projects.roles.viewer') || 'Viewer' }
                    ]}
                    {...field}
                    onChange={(e) => {
                      const selectedRole = e.target.value;
                      field.onChange(e);
                      const rolePermissions = getRolePermissions(selectedRole);
                      Object.keys(rolePermissions).forEach(key => {
                        setMemberValueAdd(`permissions.${key}`, rolePermissions[key]);
                      });
                    }}
                  />
                )}
              />

              <div className="border-t border-gray-200 pt-4">
                <p className="text-sm font-medium text-gray-700 mb-3">{t('common.permissions') || 'Permissions'}</p>
                <div className="space-y-2">
                  {[
                    { key: 'canEditProject', label: t('projects.permissions.editProject') || 'Edit Project' },
                    { key: 'canManageFiles', label: t('projects.permissions.manageFiles') || 'Manage Files' },
                    { key: 'canManageNotes', label: t('projects.permissions.manageNotes') || 'Manage Notes' },
                    { key: 'canManageTasks', label: t('projects.permissions.manageTasks') || 'Manage Tasks' },
                    { key: 'canManageMilestones', label: t('projects.permissions.manageMilestones') || 'Manage Milestones' },
                    { key: 'canManageMembers', label: t('projects.permissions.manageMembers') || 'Manage Members' },
                    { key: 'canViewFinancials', label: t('projects.permissions.viewFinancials') || 'View Financials' },
                    { key: 'canChat', label: t('projects.permissions.chat') || 'Chat' }
                  ].map(perm => (
                    <label key={perm.key} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                      <span className="text-sm text-gray-700">{perm.label}</span>
                      <input
                        type="checkbox"
                        {...registerMemberAdd(`permissions.${perm.key}`)}
                        className="rounded"
                      />
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" onClick={() => { setShowAddMemberModal(false); resetMemberAdd(); }}>
              {t('common.cancel')}
            </Button>
            <Button type="submit">{t('projects.addMember') || 'Add Member'}</Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Edit Member Modal */}
      <Modal isOpen={showEditMemberModal} onClose={() => { setShowEditMemberModal(false); setSelectedMember(null); resetMemberEdit(); }} title={t('projects.editMember') || 'Edit Team Member'}>
        <form onSubmit={handleMemberSubmitEdit(onUpdateMember)}>
          <ModalBody>
            <div className="space-y-4">
              {/* Display member info */}
              {selectedMember && selectedMember.user && (
                <div className="p-3 bg-gray-50 rounded-lg mb-4">
                  <div className="flex items-center gap-3">
                    {/* if selected member avatar exist show it if not display user icon */}
                    {selectedMember?.user?.avatar ? (
                      <Avatar
                        src={selectedMember.user.avatar ? (selectedMember.user.avatar.startsWith('http') ? selectedMember.user.avatar : process.env.REACT_APP_SOCKET_URL + selectedMember.user.avatar) : null}
                        name={`${selectedMember.user.firstName} ${selectedMember.user.lastName}`}
                        size="sm"
                      />
                    ) : (
                      <UserIcon className="w-4 h-4" />
                    )}
                    <div>
                      <p className="font-medium text-sm">
                        {selectedMember.user.firstName} {selectedMember.user.lastName}
                      </p>
                      {selectedMember.user.email && (
                        <p className="text-xs text-gray-500">{selectedMember.user.email}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <Controller
                name="role"
                control={controlMemberEdit}
                render={({ field }) => (
                  <Select
                    label={t('projects.role') || 'Role'}
                    options={[
                      { value: 'manager', label: t('projects.roles.manager') || 'Manager' },
                      { value: 'contributor', label: t('projects.roles.contributor') || 'Contributor' },
                      { value: 'viewer', label: t('projects.roles.viewer') || 'Viewer' }
                    ]}
                    {...field}
                    onChange={(e) => {
                      const selectedRole = e.target.value;
                      field.onChange(e);
                      const rolePermissions = getRolePermissions(selectedRole);
                      Object.keys(rolePermissions).forEach(key => {
                        setMemberValueEdit(`permissions.${key}`, rolePermissions[key]);
                      });
                    }}
                  />
                )}
              />

              <div className="border-t border-gray-200 pt-4">
                <p className="text-sm font-medium text-gray-700 mb-3">{t('common.permissions') || 'Permissions'}</p>
                <div className="space-y-2">
                  {[
                    { key: 'canEditProject', label: t('projects.permissions.editProject') || 'Edit Project' },
                    { key: 'canManageFiles', label: t('projects.permissions.manageFiles') || 'Manage Files' },
                    { key: 'canManageNotes', label: t('projects.permissions.manageNotes') || 'Manage Notes' },
                    { key: 'canManageTasks', label: t('projects.permissions.manageTasks') || 'Manage Tasks' },
                    { key: 'canManageMilestones', label: t('projects.permissions.manageMilestones') || 'Manage Milestones' },
                    { key: 'canManageMembers', label: t('projects.permissions.manageMembers') || 'Manage Members' },
                    { key: 'canViewFinancials', label: t('projects.permissions.viewFinancials') || 'View Financials' },
                    { key: 'canChat', label: t('projects.permissions.chat') || 'Chat' }
                  ].map(perm => (
                    <label key={perm.key} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                      <span className="text-sm text-gray-700">{perm.label}</span>
                      <input
                        type="checkbox"
                        {...registerMemberEdit(`permissions.${perm.key}`)}
                        className="rounded"
                      />
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" type="button" onClick={() => { setShowEditMemberModal(false); setSelectedMember(null); resetMemberEdit(); }}>
              {t('common.cancel')}
            </Button>
            <Button type="submit">{t('common.update')}</Button>
          </ModalFooter>
        </form>
      </Modal>
    </>
  );
}